import { useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/mockups/ui";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Trash2, CalendarDays, List, LayoutGrid } from "lucide-react";

const SCOPE = {
  nacional: { label: "Nacional", cls: "bg-primary/12 text-primary", dot: "bg-primary", cal: "bg-primary/15 text-primary ring-1 ring-primary/40" },
  "autonómico": { label: "C. de Madrid", cls: "bg-chart-4/12 text-chart-4", dot: "bg-chart-4", cal: "bg-chart-4/15 text-chart-4 ring-1 ring-chart-4/40" },
  local: { label: "Local (Madrid)", cls: "bg-gold/15 text-[#a9741c] dark:text-gold", dot: "bg-gold", cal: "bg-gold/20 text-[#a9741c] ring-1 ring-gold/50" },
};
const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "2-digit", month: "long" });
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DOW = ["L", "M", "X", "J", "V", "S", "D"];

function MonthCal({ year, month, holidays }) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // lunes = 0
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div className="border border-border rounded-lg p-3">
      <div className="text-sm font-heading font-bold mb-2">{MONTHS[month]}</div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {DOW.map((w, i) => <div key={i} className="text-[10px] font-semibold text-muted-foreground/70 pb-1">{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const h = holidays[iso];
          const sc = h ? (SCOPE[h.scope] || SCOPE.local) : null;
          return (
            <div
              key={i}
              title={h ? h.name : ""}
              data-testid={h ? `cal-holiday-${iso}` : undefined}
              className={cn(
                "text-[11px] h-7 flex items-center justify-center rounded-md tabular-nums",
                sc ? `font-bold cursor-default ${sc.cal}` : "text-foreground/70"
              )}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HolidayDialog({ open, onOpenChange, year, onSaved }) {
  const [form, setForm] = useState({ date: `${year}-01-01`, name: "", scope: "local" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.date) { toast.error("Indica fecha y nombre"); return; }
    setSaving(true);
    try {
      await api.post("/hr/holidays", form);
      toast.success("Festivo añadido");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Añadir festivo</DialogTitle><DialogDescription>Los festivos se descuentan al calcular los días de vacaciones.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="holiday-date-input" /></div>
          <div className="space-y-1.5"><Label>Nombre</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="holiday-name-input" /></div>
          <div className="space-y-1.5">
            <Label>Ámbito</Label>
            <Select value={form.scope} onValueChange={(v) => set("scope", v)}>
              <SelectTrigger data-testid="holiday-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nacional">Nacional</SelectItem>
                <SelectItem value="autonómico">Comunidad de Madrid</SelectItem>
                <SelectItem value="local">Local (Madrid)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="holiday-save-btn">{saving ? "Guardando…" : "Añadir"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Festivos({ embedded = false }) {
  const { can } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data: holidays, loading, refetch } = useFetch("/hr/holidays", { year });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [view, setView] = useState("list");
  const years = [year - 1, year, year + 1].filter((v, i, a) => a.indexOf(v) === i);

  const holidaysByDate = {};
  (holidays || []).forEach((h) => { holidaysByDate[h.date] = h; });

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este festivo?")) return;
    try { await api.delete(`/hr/holidays/${id}`); toast.success("Festivo eliminado"); refetch(); }
    catch (e) { toast.error("Error"); }
  };

  const addBtn = can("hr", "create") && (
    <Button size="sm" className="gap-1.5" onClick={() => setDialogOpen(true)} data-testid="add-holiday-btn"><Plus className="h-4 w-4" /> Añadir festivo</Button>
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="festivos-page">
      {embedded ? (
        addBtn && <div className="flex items-center justify-end">{addBtn}</div>
      ) : (
        <PageHeader title="Festivos" subtitle="Calendario laboral (nacional, Comunidad de Madrid y Madrid capital)">
          {addBtn}
        </PageHeader>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32" data-testid="year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <div className="inline-flex rounded-md border border-border overflow-hidden">
          <button onClick={() => setView("list")} data-testid="festivos-view-list" className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors", view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")}><List className="h-4 w-4" /> Lista</button>
          <button onClick={() => setView("calendar")} data-testid="festivos-view-calendar" className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors border-l border-border", view === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")}><LayoutGrid className="h-4 w-4" /> Calendario</button>
        </div>
      </div>

      {view === "calendar" ? (
        loading || !holidays ? (
          <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : (
          <div className="space-y-4" data-testid="festivos-calendar">
            <div className="flex flex-wrap gap-3">
              {Object.entries(SCOPE).map(([k, s]) => (
                <span key={k} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><span className={cn("h-2.5 w-2.5 rounded-full", s.dot)} /> {s.label}</span>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {MONTHS.map((_, m) => <MonthCal key={m} year={year} month={m} holidays={holidaysByDate} />)}
            </div>
          </div>
        )
      ) : (
      <Card className="border-border shadow-none overflow-hidden">
        {loading || !holidays ? (
          <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : holidays.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No hay festivos cargados para {year}.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Fecha</TableHead>
                <TableHead>Festivo</TableHead>
                <TableHead>Ámbito</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {holidays.map((h) => {
                const sc = SCOPE[h.scope] || { label: h.scope, cls: "bg-muted text-muted-foreground" };
                return (
                  <TableRow key={h.id} data-testid={`holiday-row-${h.id}`}>
                    <TableCell className="pl-5">
                      <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0"><CalendarDays className="h-4 w-4 text-muted-foreground" /></div>
                        <span className="text-sm font-medium capitalize tabular-nums">{fmt(h.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{h.name}</TableCell>
                    <TableCell><Badge variant="outline" className={cn("border-transparent font-medium", sc.cls)}>{sc.label}</Badge></TableCell>
                    <TableCell>
                      {can("hr", "delete") && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(h.id)} data-testid={`holiday-del-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
      )}

      {dialogOpen && <HolidayDialog open={dialogOpen} onOpenChange={setDialogOpen} year={year} onSaved={refetch} />}
    </div>
  );
}
